/**
 * Public blog API helpers.
 * Works in the browser and during SSR (Node global fetch) without bundling
 * firebase-admin into the client.
 */

import { apiBaseUrl } from './api';
import { BLOG_PAGE_SIZE } from './blog';

/**
 * Resolve API origin for browser or SSR.
 * SSR prefers loopback so HTTP fallback does not depend on public hostname DNS.
 *
 * @returns {string}
 */
const getBlogApiBase = () => {
  if (typeof window !== 'undefined') {
    return apiBaseUrl();
  }
  const apiPort = process.env.REACT_APP_DEV_API_SERVER_PORT;
  if (process.env.NODE_ENV === 'development' && apiPort) {
    return `http://127.0.0.1:${apiPort}`;
  }
  const port = process.env.PORT;
  if (port) {
    return `http://127.0.0.1:${port}`;
  }
  return (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');
};

const blogFetch = async path => {
  const url = `${getBlogApiBase()}${path}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (res.status === 404) {
    const err = new Error('not_found');
    err.status = 404;
    throw err;
  }
  if (!res.ok) {
    const err = new Error('blog_fetch_failed');
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/**
 * Fetch a page of published posts.
 *
 * @param {number} [page=1]
 * @param {number} [pageSize]
 * @returns {Promise<{ posts: Object[], pagination: Object }>}
 */
export const fetchBlogPosts = (page = 1, pageSize = BLOG_PAGE_SIZE) => {
  return blogFetch(`/api/blog-posts?page=${page}&pageSize=${pageSize}`);
};

/**
 * Fetch a single published post by slug.
 *
 * @param {string} slug
 * @returns {Promise<{ post: Object }>}
 */
export const fetchBlogPostBySlug = slug => {
  return blogFetch(`/api/blog-posts/${encodeURIComponent(slug)}`);
};
